/**
 * UI Store - Manages UI state
 *
 * Handles toasts, modals, tabs, and other UI concerns.
 */

import { signal, computed } from '@preact/signals';
import type { Tab, Toast, Modal } from '@/types';

// Re-export Tab type for convenience
export type { Tab } from '@/types';

// ============================================
// TABS
// ============================================

export const activeTab = signal<Tab>('discover');

export function setTab(tab: Tab): void {
  activeTab.value = tab;
  // Update URL without reload
  const url = new URL(window.location.href);
  url.searchParams.set('tab', tab);
  window.history.replaceState({}, '', url.toString());
}

// Load tab from URL on init
export function initTabFromUrl(): void {
  const url = new URL(window.location.href);
  const tab = url.searchParams.get('tab') as Tab | null;
  if (tab && ['discover', 'submit', 'leaderboard', 'archives', 'pods'].includes(tab)) {
    activeTab.value = tab;
  }
}

// ============================================
// TOASTS
// ============================================

export const toasts = signal<Toast[]>([]);

let toastId = 0;

export function showToast(
  message: string,
  type: Toast['type'] = 'info',
  duration = 4000
): string {
  const id = `toast-${++toastId}`;

  const toast: Toast = { id, type, message, duration };
  toasts.value = [...toasts.value, toast];

  // Auto-remove after duration
  if (duration > 0) {
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }

  return id;
}

export function removeToast(id: string): void {
  toasts.value = toasts.value.filter(t => t.id !== id);
}

// Convenience methods
export const toast = {
  success: (message: string, duration?: number) => showToast(message, 'success', duration),
  error: (message: string, duration?: number) => showToast(message, 'error', duration),
  info: (message: string, duration?: number) => showToast(message, 'info', duration),
  warning: (message: string, duration?: number) => showToast(message, 'warning', duration),
};

// ============================================
// MODALS
// ============================================

export const modals = signal<Modal[]>([]);

let modalId = 0;

export function openModal(component: string, props?: Record<string, unknown>): string {
  const id = `modal-${++modalId}`;
  modals.value = [...modals.value, { id, component, props }];
  // Prevent body scroll
  document.body.style.overflow = 'hidden';
  return id;
}

export function closeModal(id?: string): void {
  if (id) {
    modals.value = modals.value.filter(m => m.id !== id);
  } else {
    // Close top modal
    modals.value = modals.value.slice(0, -1);
  }

  // Restore body scroll if no modals
  if (modals.value.length === 0) {
    document.body.style.overflow = '';
  }
}

export function closeAllModals(): void {
  modals.value = [];
  document.body.style.overflow = '';
}

export const hasOpenModal = computed(() => modals.value.length > 0);

// ============================================
// SIDEBAR / MOBILE MENU
// ============================================

export const isSidebarOpen = signal(false);
export const isMobileMenuOpen = signal(false);

export function toggleSidebar(): void {
  isSidebarOpen.value = !isSidebarOpen.value;
}

export function toggleMobileMenu(): void {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
}

export function closeMobileMenu(): void {
  isMobileMenuOpen.value = false;
}

// ============================================
// LOADING STATES
// ============================================

export const isAppLoading = signal(true);
export const loadingMessage = signal<string | null>(null);

export function setAppLoading(loading: boolean, message?: string): void {
  isAppLoading.value = loading;
  loadingMessage.value = message || null;
}

// ============================================
// THEME
// ============================================

export type Theme = 'dark' | 'light' | 'system';
export const theme = signal<Theme>('dark');

export function setTheme(newTheme: Theme): void {
  theme.value = newTheme;
  localStorage.setItem('sherit_theme', newTheme);

  // Apply to document
  if (newTheme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  } else {
    document.documentElement.setAttribute('data-theme', newTheme);
  }
}

export function initTheme(): void {
  const saved = localStorage.getItem('sherit_theme') as Theme | null;
  if (saved) {
    setTheme(saved);
  } else {
    setTheme('dark');
  }
}

// ============================================
// RESPONSIVE
// ============================================

export const isMobile = signal(false);
export const isTablet = signal(false);

function updateBreakpoints(): void {
  isMobile.value = window.innerWidth < 640;
  isTablet.value = window.innerWidth >= 640 && window.innerWidth < 1024;
}

export function initResponsive(): void {
  updateBreakpoints();
  window.addEventListener('resize', updateBreakpoints);
}

// ============================================
// SEARCH
// ============================================

export const isSearchOpen = signal(false);
export const searchQuery = signal('');

export function openSearch(): void {
  isSearchOpen.value = true;
}

export function closeSearch(): void {
  isSearchOpen.value = false;
  searchQuery.value = '';
}

// ============================================
// SETTINGS PANEL
// ============================================

export const isSettingsOpen = signal(false);

export function openSettings(): void {
  isSettingsOpen.value = true;
}

export function closeSettings(): void {
  isSettingsOpen.value = false;
}

export function toggleSettings(): void {
  isSettingsOpen.value = !isSettingsOpen.value;
}

// ============================================
// LOGIN PROMPT MODAL
// ============================================

export const showLoginPrompt = signal(false);

export function openLoginPrompt(): void {
  showLoginPrompt.value = true;
}

export function hideLoginPrompt(): void {
  showLoginPrompt.value = false;
}
